'use client'

// The boards and sheets people put things on.
//
// Two models, deliberately two: adPosters.glb is space somebody bought,
// eventPosters.glb is space somebody earned. They hang in different places
// and they are different objects, so nothing bought can be dressed up as
// something happening.
//
// The rule that shapes this whole component: a surface with nothing on it
// is HIDDEN, not blank. An empty white rectangle bolted to a wall reads as
// a broken game, while an absent one just reads as a wall. So every art
// face starts invisible and only appears once a texture has actually
// decoded. Billboards are the exception, because they carry placeholder
// art in the model: an unsold billboard is still a billboard.
//
// Both GLBs ship with either one shared material or none at all, which
// would mean every board showing the same poster (or no board showing
// anything). Each art face is given its own material here, at load, so
// Sam does not have to re-export six meshes to change that.

import { useEffect, useMemo, useState } from 'react'
import { useThree } from '@react-three/fiber'
import * as THREE from 'three/webgpu'
import { useModel, getKTX2 } from '../hooks/useModel'
import { toNode } from '../hooks/useSceneNodeMaterials'
import { ditherMaterial } from '../effects/ditherRegistry'
import { useDeadScreen } from './DeadScreen'
import { DEAD_SCREEN_SLOTS } from '../../../lib/ads/deadScreens'
import { uvsMatchAxes, planarUVsFor } from '../../../lib/world/quadUVs'

const AD_MODEL = '/models/adPosters.glb'
const EVENT_MODEL = '/models/eventPosters.glb'
const POLL_MS = 120_000

// Node names that carry art, mapped to the slot ids the API answers with.
// The GLB names ARE the slot ids for adverts (kept deliberately in step,
// see lib/ads/slots.js); the event model names its faces the long way.
const AD_FACES = {
  billboard_salon_screen: 'billboard_salon',
  billboard_museum_screen: 'billboard_museum',
  poster_laundrette: 'poster_laundrette',
  poster_coffee: 'poster_coffee',
  poster_phone: 'poster_phone',
  poster_pond: 'poster_pond',
}
const EVENT_FACES = {
  event_poster_museum: 'museum_bill',
  event_poster_cinema: 'cinema_bill',
  event_poster_rave: 'rave_sheet',
  event_poster_bar: 'bar_sheet',
}

// A billboard keeps SOMETHING when unsold; a paper sheet does not,
// because a blank sheet is litter and a blank billboard is a billboard.
//
// What it keeps is his shader now (see DeadScreen.jsx). The blank art
// below is the fallback for anyone without WebGPU, and for the moment
// before the compute pipeline has finished building, so a board is never
// a white rectangle while it waits.
//
// The art is a loose KTX2 rather than baked into the GLB so Sam can swap
// what an unsold board is showing without re-exporting a model, and so the
// two billboards can advertise different nonsense. KTX2 for the same reason
// every other texture here is: it stays compressed in VRAM. A board with no
// entry stays visible with whatever the model ships.
const KEEPS_PLACEHOLDER = new Set(['billboard_salon_screen', 'billboard_museum_screen'])
//
// The .png beside each .ktx2 is the master. To re-encode after changing one
// (dimensions must be multiples of 4, which is why 570 became 568):
//
//   sharp(src).resize(1024, 568, { fit: 'fill' }).png() -> in.png
//   toktx --t2 --encode etc1s --genmipmap --assign_oetf srgb out.ktx2 in.png
//
// Same settings lib/glb/optimize.mjs uses for model textures. UASTC was 6.6x
// the size for no visible gain on this art.
const PLACEHOLDER_ART = {
  // Both boards run blank while their real art is being made. The
  // adverts behind them are already live: the sim carries the NOTE,
  // which is the only part he can actually perceive, and the picture is
  // for the people walking past. A near-black rather than pure black,
  // because these surfaces are lit and #000 reads as a hole in the world
  // rather than as a blank hoarding.
  //
  // The Outlander art is still in the folder; putting it back is one
  // line. Real posters replace all of this the moment a slot has an
  // imageUrl (see below), so this only ever shows on an unsold board.
  billboard_salon_screen: '/images/posters/billboard_blank.ktx2',
  billboard_museum_screen: '/images/posters/billboard_blank.ktx2',
}

// Decoded once for the life of the page, not once per effect run. This
// effect re-runs every time the ad poll returns, and without this the
// board refetched and re-transcoded the same static file every two
// minutes forever. Shared, so cleanup must leave these alone.
const _placeholders = new Map()
function placeholderTexture(ktx2, url) {
  if (!_placeholders.has(url)) {
    _placeholders.set(url, ktx2.loadAsync(url).then((tex) => {
      tex.colorSpace = THREE.SRGBColorSpace
      tex.anisotropy = 4
      // No flipY: a compressed texture cannot be flipped on the GPU, so
      // orientation is settled at encode time. The file is written
      // KTXorientation rd (top-down), which is what glTF UVs expect.
      tex.userData.shared = true
      return tex
    }))
  }
  return _placeholders.get(url)
}

// ── UVs ─────────────────────────────────────────────────────────────────
//
// Every poster mesh in both GLBs is unwrapped properly. None of the four
// billboard meshes are: billboard_salon_screen has the same v on all four
// corners and a u running -2.4 to 4.0 mapped to HEIGHT rather than width,
// and billboard_museum_screen is the same shape of wrong. A texture on
// either would come out as one smeared row of pixels tiled sideways.
//
// Nobody noticed because until now nothing had ever been put on them.
//
// They are flat four-corner quads, so a planar projection built from the
// corners themselves is not an approximation, it is exactly what a correct
// unwrap would produce. It only runs on meshes whose UVs are actually
// unusable, so re-exporting a properly unwrapped billboard makes this step
// aside on its own rather than fighting it.

function uvsAreUsable(geo) {
  const uv = geo.getAttribute('uv')
  if (!uv) return false
  let uMin = Infinity, uMax = -Infinity, vMin = Infinity, vMax = -Infinity
  for (let i = 0; i < uv.count; i++) {
    const u = uv.getX(i), v = uv.getY(i)
    if (u < uMin) uMin = u
    if (u > uMax) uMax = u
    if (v < vMin) vMin = v
    if (v > vMax) vMax = v
  }
  // In range, and actually spanning the image in both directions.
  return uMin >= -0.01 && uMax <= 1.01 && vMin >= -0.01 && vMax <= 1.01 &&
         uMax - uMin > 0.5 && vMax - vMin > 0.5
}

/**
 * The world-space corners and normal of a flat quad, which is everything
 * lib/world/quadUVs.js needs to answer questions about its unwrap. The
 * maths lives there rather than here so it can be run against the real
 * billboard numbers in a test: this file cannot be imported by one.
 */
function quadOf(mesh) {
  const geo = mesh.geometry
  const pos = geo.getAttribute('position')
  const uvAttr = geo.getAttribute('uv')
  if (!pos || pos.count !== 4) return null

  const normal = new THREE.Vector3()
  const nAttr = geo.getAttribute('normal')
  if (nAttr) normal.fromBufferAttribute(nAttr, 0)
  else normal.set(0, 0, 1)
  normal.transformDirection(mesh.matrixWorld).normalize()

  const p = new THREE.Vector3()
  const corners = []
  const uvs = []
  for (let i = 0; i < 4; i++) {
    p.fromBufferAttribute(pos, i).applyMatrix4(mesh.matrixWorld)
    corners.push([p.x, p.y, p.z])
    if (uvAttr) uvs.push([uvAttr.getX(i), uvAttr.getY(i)])
  }
  return { corners, uvs, normal: [normal.x, normal.y, normal.z] }
}

/** Does u track the quad's right axis and v its down axis? */
function uvsRightWayRound(mesh) {
  const q = quadOf(mesh)
  if (!q || q.uvs.length !== 4) return true   // not a flat quad: not ours to judge
  return uvsMatchAxes(q.corners, q.uvs, q.normal)
}

/**
 * Give a flat quad the UVs it should have shipped with.
 *
 * v runs 0 at the TOP because that is the glTF convention every texture
 * here is loaded under (flipY false), and it is the only option for the
 * KTX2 placeholder: a compressed texture cannot be flipped on the GPU.
 */
function planarUVs(mesh) {
  const q = quadOf(mesh)
  if (!q) return false
  const uv = planarUVsFor(q.corners, q.normal)
  if (!uv) return false
  // Cloned first: scene.clone(true) shares geometry with the cached GLB,
  // and writing through would edit what every other mount receives.
  mesh.geometry = mesh.geometry.clone()
  mesh.geometry.setAttribute('uv', new THREE.BufferAttribute(uv, 2))
  return true
}

export function AdBoards() {
  const [live, setLive] = useState(null)
  // One simulation for every dead screen in the town, built here rather
  // than per board: the grid is the picture, not the surface.
  const deadScreen = useDeadScreen()

  useEffect(() => {
    let alive = true
    const read = () => {
      fetch('/api/ads/live', { cache: 'no-store' })
        .then((r) => r.json())
        .then((b) => {
          if (!alive) return
          // Only when something actually changed. Every poll used to set
          // a fresh object, the hanging effect re-ran on it, and all the
          // board materials were torn down and rebuilt every two minutes:
          // up to three seconds outside the dither pass each time, which
          // is the flicker Sam kept catching on the posters. Identical
          // payload, identical state, no rebuild.
          setLive((prev) => (prev && JSON.stringify(prev) === JSON.stringify(b) ? prev : b))
        })
        .catch(() => { /* the walls stay as they are */ })
    }
    read()
    const id = setInterval(read, POLL_MS)
    return () => { alive = false; clearInterval(id) }
  }, [])

  return (
    <>
      <Boards url={AD_MODEL} faces={AD_FACES} showing={live?.ads} deadScreen={deadScreen} />
      {/* No dead screens on the event model: an event poster with nothing
          on it comes down, so there is no blank surface to run anything on. */}
      <Boards url={EVENT_MODEL} faces={EVENT_FACES} showing={live?.events} />
    </>
  )
}

function Boards({ url, faces, showing, deadScreen = null }) {
  const { scene } = useModel(url)
  const gl = useThree((st) => st.gl)
  // Cloned so two mounts could never share a material, and so the cached
  // GLB the loader hands out is never mutated.
  const root = useMemo(() => scene.clone(true), [scene])

  // One effect on purpose. Splitting "give each face its own material" from
  // "hang what is up" meant the second reached back into meshes the first
  // had set up, which is a shared mutable across effects: the React
  // compiler rejects it, and it is genuinely the harder version to reason
  // about. Setup is idempotent instead, and runs with the hanging.
  useEffect(() => {
    const loader = new THREE.TextureLoader()
    loader.setCrossOrigin('anonymous')
    const ktx2 = getKTX2(gl)
    let alive = true
    const mine = []

    // planarUVs projects from world positions, so the matrices have to be
    // current: on the first effect pass they have not been composed yet.
    root.updateMatrixWorld(true)

    const frames = new Map()
    // Detachers for the boards handed his shader. The material itself is
    // the hook's and outlives this effect, so nothing here disposes it;
    // all cleanup owes is to stop the frame loop testing a dead mesh.
    const lentOut = []
    root.traverse((o) => {
      if (!o.isMesh) return
      const slot = faces[o.name]
      if (!slot) {
        // The frame and posts around a board carry no art, but they are
        // a surface in this world like any other: node material, dithered
        // at birth. Left as the loader's classic material (which is what
        // happened until Sam textured them) they rendered outside the
        // dither pass, glossy and un-dotted next to everything else.
        if (!o.userData.ownMaterial) {
          const nm = toNode(o.material, frames)
          if (nm !== o.material) { ditherMaterial(nm); o.material = nm }
          o.userData.ownMaterial = true
        }
        return
      }
      // Rebuilt on every poll that changes something; the Precompiler
      // builds the new pipeline off the frame instead of the first look.
      o.userData.precompile = true

      if (!o.userData.ownGeometry) {
        // Before the material, because a mesh that cannot be textured is
        // not worth giving its own material to.
        if (!uvsAreUsable(o.geometry) || !uvsRightWayRound(o)) {
          // Keep the original so cleanup can hand it back. This effect
          // re-runs every time the poll returns, and disposing the clone
          // without restoring would leave the mesh holding a dead buffer
          // that the next pass reads as already-fixed.
          const src = o.geometry
          if (planarUVs(o)) o.userData.srcGeometry = src
        }
        o.userData.ownGeometry = true
      }

      // Unsold, and one of the two screens he wrote the shader for. It
      // gets his program rather than a material of its own: one grid,
      // both boards, compiled once. Off WebGPU deadScreen.material stays
      // null and this falls through to the blank placeholder below.
      const item = showing?.[slot]
      const dead = !item?.imageUrl
        && DEAD_SCREEN_SLOTS.includes(slot)
        && !!deadScreen?.material
      if (dead) {
        // Anything it was holding first: a board that had a paid poster
        // on it must not keep the texture underneath his pattern.
        if (o.userData.ownMaterial) {
          if (o.material?.map && !o.material.map.userData?.shared) o.material.map.dispose()
          o.material?.dispose()
          o.userData.ownMaterial = false
        }
        o.material = deadScreen.material
        o.visible = true
        o.userData.precompile = true
        // Under the visibility test, which is what keeps the compute
        // from running while nobody is looking at either board.
        const off = deadScreen.attach(o)
        if (off) lentOut.push(off)
        // Still tracked: the cloned geometry from the UV fix above is
        // this effect's and has to be handed back on the way out. Only
        // the material is somebody else's, and cleanup checks for that.
        mine.push(o)
        return
      }

      if (!o.userData.ownMaterial) {
        // A NODE material, like every other surface in this world. The
        // classic MeshStandardMaterial that used to be here renders fine
        // on its own, which is why it went unnoticed, but the dither pass
        // only sees node materials, so the boards and every poster on
        // them were quietly sitting outside the render. ArtWall, Fences
        // and Phone all convert their GLB materials the same way.
        const nm = new THREE.MeshStandardNodeMaterial()
        // Painted paper and printed board are matte. The GLB materials
        // export semi-gloss, which catches the sun and reads as plastic.
        nm.roughness = 0.92
        nm.metalness = 0
        nm.color = new THREE.Color('white')
        // Carried over, not chosen here. Every billboard mesh is authored
        // double-sided and the salon one has its screen facing away from
        // the salon, so defaulting to FrontSide culled it: from the place
        // he actually stands, the board had nothing on it at all. Read
        // BEFORE the assignment below, or it reads back the new material.
        nm.side = o.material?.side ?? THREE.FrontSide
        // At birth, not on the next sweep: a board rebuilt mid-session
        // must never show a clean frame first.
        ditherMaterial(nm)
        o.material = nm
        o.userData.ownMaterial = true
      }
      mine.push(o)

      if (!item?.imageUrl) {
        // Nothing up. A billboard keeps a placeholder because an unsold
        // billboard is still a billboard; a paper sheet goes away, because
        // a blank sheet is litter.
        o.visible = KEEPS_PLACEHOLDER.has(o.name)
        const art = PLACEHOLDER_ART[o.name]
        if (o.visible && art) {
          placeholderTexture(ktx2, art)
            .then((tex) => {
              if (!alive) return
              o.material.map = tex
              o.material.needsUpdate = true
            })
            .catch(() => { /* an unsold board without its art is a blank board */ })
        }
        return
      }

      // Something sold. Anything the placeholder left on the material has
      // to go, or a texture that fails to load would leave the old art up
      // under a slot somebody has paid for.
      if (o.material.map) {
        if (!o.material.map.userData?.shared) o.material.map.dispose()
        o.material.map = null
      }

      loader.load(
        item.imageUrl,
        (tex) => {
          if (!alive) return
          tex.colorSpace = THREE.SRGBColorSpace
          tex.flipY = false          // glTF UV convention, as everywhere else here
          // The look the sweep would force anyway, applied before the
          // first frame instead of a few seconds after it: new art used
          // to hang smooth-filtered and then snap to the pixel look.
          tex.magFilter = THREE.NearestFilter
          tex.minFilter = THREE.NearestFilter
          o.material.map = tex
          o.material.needsUpdate = true
          // Shown only now: revealing the face before its texture decodes
          // is one frame of white, which is the thing being avoided.
          o.visible = true
        },
        undefined,
        () => { /* a poster that will not load stays down */ },
      )
    })

    return () => {
      alive = false
      // The material belongs to the hook, so nothing here disposes it.
      // Detaching is all that is owed: it stops the frame loop testing a
      // mesh that is on its way out.
      for (const off of lentOut) off()
      for (const o of mine) {
        // Placeholder art outlives this effect on purpose, so only free a
        // map we actually own. Disposing the shared one would blank every
        // billboard until the next poll re-decoded it. Same argument for
        // the material itself now a board can be wearing his shader,
        // which belongs to the hook and is shared between both screens.
        if (o.userData.ownMaterial) {
          if (o.material?.map && !o.material.map.userData?.shared) o.material.map.dispose()
          o.material?.dispose()
        }
        o.userData.ownMaterial = false
        // Give the shared geometry back and free our clone. Only ever set
        // when planarUVs actually cloned, so this can never dispose the
        // GLB's own buffer.
        if (o.userData.srcGeometry) {
          o.geometry?.dispose()
          o.geometry = o.userData.srcGeometry
          o.userData.srcGeometry = null
        }
        o.userData.ownGeometry = false
      }
    }
  }, [root, faces, showing, gl, deadScreen])

  return <primitive object={root} />
}

export default AdBoards
